import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const params = useParams<{ slug: string }>();
  const slug = params.slug || '';
  const category = categories.find(c => c.slug === slug);
  const categoryArticles = getArticlesByCategory(slug);

  // Schema.org structured data
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://51heilian.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: category?.name || '分类',
        item: `https://51heilian.com/category/${slug}`,
      },
    ],
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: `https://51heilian.com/category/${slug}`,
    name: `${category?.name || '分类'} - 51黑料不打烊`,
    description: `浏览所有${category?.name || '分类'}相关爆料内容，覆盖51黑料在线观看、吃瓜视频等热门内容。`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      url: 'https://51heilian.com/',
      name: '51黑料不打烊',
    },
  };

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-24 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-heading text-foreground-950 mb-4">分类不存在</h1>
            <a href="/" className="text-primary-500 hover:text-primary-400 transition-colors">返回首页</a>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }} />
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative pt-24 md:pt-32 pb-10 md:pb-16 px-4 md:px-6 lg:px-10 text-center">
          <nav className="flex items-center justify-center gap-2 text-sm text-foreground-400 mb-4" aria-label="面包屑导航">
            <a href="/" className="hover:text-accent-500 transition-colors">首页</a>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-accent-500">{category.name}</span>
          </nav>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-black text-foreground-950 leading-tight">
            {category.name}
          </h1>
          <p className="font-heading text-xl md:text-2xl text-primary-500 italic mt-2">51黑料不打烊</p>
          <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto mt-4">
            浏览所有<strong className="text-accent-500">{category.name}</strong>相关爆料，持续更新中
          </p>
        </section>

        {/* Feature Grid */}
        {categoryArticles.length >= 3 && (
          <section className="px-4 md:px-6 lg:px-10 pb-10 md:pb-16">
            <div className="max-w-[1400px] mx-auto">
              <div className="flex flex-col lg:flex-row gap-4 md:gap-5">
                <div className="lg:w-1/2">
                  <a href={`/article/${categoryArticles[0].id}`} className="card-dark group h-full block">
                    <div className="relative h-full min-h-[350px] md:min-h-[450px] overflow-hidden">
                      <img src={categoryArticles[0].image} alt={categoryArticles[0].title} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/30 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                        <span className="bg-primary-500/30 text-primary-300 tag-pill mb-3">本周最热</span>
                        <h3 className="text-foreground-950 font-heading text-xl md:text-3xl leading-snug line-clamp-2">{categoryArticles[0].title}</h3>
                        <p className="text-foreground-400 text-sm mt-2 line-clamp-2">{categoryArticles[0].excerpt}</p>
                      </div>
                    </div>
                  </a>
                </div>
                <div className="lg:w-1/2 flex flex-col gap-4 md:gap-5">
                  {categoryArticles.slice(1, 3).map((article, i) => (
                    <a key={article.id} href={`/article/${article.id}`} className="card-dark group flex-1">
                      <div className="relative h-full min-h-[170px] md:min-h-[215px] overflow-hidden">
                        <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-transparent"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                          <span className={`${i === 0 ? 'bg-accent-500/30 text-accent-300' : 'bg-secondary-500/30 text-secondary-300'} tag-pill mb-2`}>
                            {i === 0 ? '视频黑料' : '图文实锤'}
                          </span>
                          <h3 className="text-foreground-950 font-heading text-lg md:text-xl leading-snug line-clamp-2">{article.title}</h3>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Article Grid */}
        <section className="px-4 md:px-6 lg:px-10 pb-14 md:pb-24">
          <div className="max-w-[1400px] mx-auto">
            <div className="mb-8 md:mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">
                全部<strong className="text-primary-500">{category.name}</strong>
              </h2>
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {categoryArticles.map((article) => (
                  <ArticleCard
                    key={article.id}
                    id={article.id}
                    title={article.title}
                    excerpt={article.excerpt}
                    category={article.category}
                    categorySlug={article.categorySlug}
                    image={article.image}
                    date={article.date}
                    views={article.views}
                    comments={article.comments}
                    tags={article.tags}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500 text-lg">该分类暂无内容，敬请期待</p>
                <a href="/" className="text-primary-500 hover:text-primary-400 transition-colors mt-4 inline-block">返回首页</a>
              </div>
            )}

            {/* Pagination */}
            {categoryArticles.length > 8 && (
              <div className="flex items-center justify-center gap-2 mt-10 md:mt-14">
                <button className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 hover:text-foreground-950 transition-colors">
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                <button className="w-10 h-10 rounded-full bg-primary-500 text-background-50 font-label text-sm">1</button>
                <button className="w-10 h-10 rounded-full bg-background-100 text-foreground-500 hover:text-foreground-950 font-label text-sm transition-colors">2</button>
                <button className="w-10 h-10 rounded-full bg-background-100 text-foreground-500 hover:text-foreground-950 font-label text-sm transition-colors">3</button>
                <span className="text-foreground-400 text-sm">...</span>
                <button className="w-10 h-10 rounded-full bg-background-100 text-foreground-500 hover:text-foreground-950 font-label text-sm transition-colors">8</button>
                <button className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 hover:text-foreground-950 transition-colors">
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}